import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Popover } from '@headlessui/react';
import { sortBy } from 'lodash';
import axiosInstance from 'apis/axiosInstance';
import AvatarPlaceHolder from 'assets/images/no-avatar.svg';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import useMatchMutate from 'hooks/useMatchMutate';
import getPractitionerName from 'utils/getPractitionerName';
import useClinicPractitioner from 'hooks/useClinicPractitioner';

interface DoctorDropdownProps {
  insuranceId: string;
  selectedDoctorIds: string[];
  isInNetwork: boolean;
  disabledDoctorIds: string[];
  disabled?: boolean;
}

const DoctorDropdown: FC<DoctorDropdownProps> = ({
  insuranceId,
  selectedDoctorIds,
  isInNetwork,
  disabledDoctorIds,
  disabled,
}) => {
  const { data: practitionersData } = useClinicPractitioner({
    shouldRun: true,
  });

  const selectAllElementRef = useRef<HTMLInputElement | null>(null);
  const [isOverflow, setIsOverflow] = useState(false);
  const dropdownRef = useRef<HTMLButtonElement>(null);
  const matchMutate = useMatchMutate();

  const [selectedIds, setSelectedIds] = useState<string[]>(selectedDoctorIds);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setSelectedIds(selectedDoctorIds);
  }, [selectedDoctorIds]);

  const selectAllId = `selectAll-doctors-${insuranceId}-${isInNetwork ? 'inn' : 'oon'}`;

  const updateDoctors = async (doctorIds: string[]) => {
    try {
      setIsSaving(true);
      await axiosInstance.patch(`/insurances/${insuranceId}`, {
        doctorIds: doctorIds,
        isInNetwork,
      });
      await matchMutate(/\/insurances/);
    } catch (error) {}
    setIsSaving(false);
  };

  const handleOnChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    doctorId: string
  ) => {
    const selectAllElement = selectAllElementRef.current;
    if (!selectAllElement) return;

    let updatedValue: string[];

    if (e.currentTarget.checked) {
      updatedValue = [...selectedIds, doctorId];
    } else {
      updatedValue = selectedIds.filter((id) => id !== doctorId);
    }

    setSelectedIds(updatedValue);
    updateDoctors(updatedValue);

    const eligibleDoctors = activeDoctors.filter(
      (d) => !disabledDoctorIds.includes(d.id)
    );
    const isAllSelected = updatedValue.length === eligibleDoctors.length;
    selectAllElement.indeterminate = !isAllSelected;
    selectAllElement.checked = isAllSelected;
  };

  const handleOnSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    let updatedValue: string[];

    if (e.currentTarget.checked) {
      updatedValue = activeDoctors
        .filter((d) => !disabledDoctorIds.includes(d.id))
        .map((d) => d.id);
    } else {
      updatedValue = [];
    }

    setSelectedIds(updatedValue);
    updateDoctors(updatedValue);
  };

  const onImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.src = AvatarPlaceHolder;
  };

  useEffect(() => {
    if (!dropdownRef.current) return;
    const dropdown = dropdownRef.current;
    const position = dropdown.getBoundingClientRect();
    const screenHeight = window.innerHeight;
    const copyPanelHeight = 280;
    const elementHeight = position.top + 26;
    const isOverflowResult = elementHeight + copyPanelHeight >= screenHeight;
    setIsOverflow(isOverflowResult);
  }, []);

  const activeDoctors = useMemo(
    () =>
      sortBy(
        (practitionersData?.data || []).filter((d) => d.isActive),
        ['firstName', 'lastName']
      ),
    [practitionersData?.data]
  );

  return (
    <div className="relative">
      <Popover>
        <Popover.Button
          disabled={disabled}
          className={`w-full h-[4.8rem] pl-1.6 rounded-[1rem] bg-white border-[0.5px] border-light-grey text-left outline-none shadow-primary border-lightest-grey ${
            disabled
              ? 'opacity-50 cursor-not-allowed bg-lightest-grey'
              : 'cursor-pointer hover:border-magenta hover:shadow-input-active focus:border-magenta focus:shadow-input-active'
          } ${
            selectedIds.length === 0 ? 'text-mid-grey' : 'text-darkest-grey'
          }`}
          ref={dropdownRef}
        >
          <span className="flex items-center gap-x-1">
            {selectedIds.length === 0
              ? 'Select Practitioners'
              : `${selectedIds.length} of ${activeDoctors.length} selected`}
            {isSaving && (
              <LoadingSpinner className="all-child:fill-magenta w-2! h-2!" />
            )}
          </span>
        </Popover.Button>
        <Popover.Panel
          className={`absolute w-full top-4.8 rounded-[0.8rem] bg-white text-base shadow-primary flex flex-col gap-y-1.6 z-20 p-2 max-h-[28rem] overflow-auto scrollbar ${
            isOverflow ? '-translate-y-[120%]' : ''
          }`}
        >
          <div className="flex gap-x-1.6 items-center">
            <input
              type="checkbox"
              className="w-1.8 h-1.8"
              id={selectAllId}
              ref={selectAllElementRef}
              onChange={handleOnSelectAll}
              checked={
                selectedIds.length ===
                  activeDoctors.filter((d) => !disabledDoctorIds.includes(d.id))
                    .length && activeDoctors.length > 0
              }
            />
            <label
              className="flex gap-x-1.6 cursor-pointer font-bold"
              htmlFor={selectAllId}
            >
              Select All
            </label>
          </div>
          {activeDoctors.map((doctor) => (
            <div key={doctor.id} className="flex gap-x-1.6 items-center">
              <input
                type="checkbox"
                className="w-1.8 h-1.8 shrink-0"
                id={`doctor-${insuranceId}-${doctor.id}-${isInNetwork ? 'inn' : 'oon'}`}
                onChange={(e) => handleOnChange(e, doctor.id)}
                checked={selectedIds.includes(doctor.id)}
                disabled={disabledDoctorIds.includes(doctor.id)}
              />
              <label
                className={`flex gap-x-1.6 cursor-pointer items-center ${disabledDoctorIds.includes(doctor.id) ? 'opacity-40 cursor-not-allowed' : ''}`}
                htmlFor={`doctor-${insuranceId}-${doctor.id}-${isInNetwork ? 'inn' : 'oon'}`}
              >
                <img
                  src={doctor.avatar || AvatarPlaceHolder}
                  alt={getPractitionerName(doctor)}
                  onError={onImageError}
                  className="rounded-full object-cover w-4 h-4 aspect-square"
                />
                <span className="text-darkest-grey text-14 leading-[2.1rem] font-bold">
                  {getPractitionerName(doctor)}
                </span>
              </label>
            </div>
          ))}
        </Popover.Panel>
      </Popover>
    </div>
  );
};

export default DoctorDropdown;
